import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  TextInput,
  Button,
  FormField,
  Alert,
  Text,
  VIPSO_RED,
} from '@vipso/shared';
import { setPendingPhone, setVerifyOtpResult } from '../store/authSlice';
import type { RootState } from '../store';
import { LoginLandingLayout, loginPrimaryButtonStyle } from '../features/auth/LoginLandingLayout';
import { DEMO_OTP, DEMO_ORGS, DEMO_USER } from '../mock/mockData';

export function OtpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const pendingPhone = useSelector((state: RootState) => state.auth.pendingPhone);
  const theme = useSelector((state: RootState) => state.auth.theme);
  const config = useSelector((state: RootState) => state.auth.config);
  const logoUrl = config?.logoUrl || theme?.logoUrl;
  const [otp, setOtp] = useState('');
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  if (!pendingPhone) {
    navigate('/login', { replace: true });
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!otp.trim() || !pendingPhone) return;

    setIsLoading(true);
    setError(false);
    try {
      if (otp.trim() !== DEMO_OTP) {
        setError(true);
        return;
      }

      const orgs = DEMO_ORGS.map((o) => ({
        orgId: o.orgId,
        name: o.name,
        membershipId: o.membershipId,
      }));

      dispatch(
        setVerifyOtpResult({
          user: {
            ...DEMO_USER,
            phone: pendingPhone,
            name: `User ${pendingPhone.slice(-4)}`,
          },
          orgs,
        }),
      );

      // Always show organization picker in the demo (3 orgs available).
      navigate('/org-picker');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleResend() {
    if (!pendingPhone) return;
    setResendLoading(true);
    try {
      dispatch(setPendingPhone(pendingPhone));
    } finally {
      setResendLoading(false);
    }
  }

  return (
    <LoginLandingLayout
      logoUrl={logoUrl}
      title={t('auth.login')}
      subtitle={t('auth.otpSentTo', { phone: pendingPhone })}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {error ? (
            <Alert color="red" title={t('auth.verificationFailed')}>
              {t('auth.invalidOtpDemo')}
            </Alert>
          ) : (
            <Text size="sm" c="dimmed" ta="center">
              {t('auth.demoHint', { otp: DEMO_OTP })}
            </Text>
          )}

          <FormField label={t('auth.verificationCode')} required>
            <TextInput
              value={otp}
              onChange={(e) => setOtp(e.currentTarget.value)}
              placeholder={t('auth.otpPlaceholder')}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={4}
            />
          </FormField>

          <Button
            variant="outline"
            onClick={() => void handleResend()}
            loading={resendLoading}
            disabled={!pendingPhone}
            fullWidth
          >
            {t('auth.resendOtp')}
          </Button>

          <Button
            type="submit"
            loading={isLoading}
            fullWidth
            disabled={!otp || otp.length !== 4}
            style={loginPrimaryButtonStyle}
          >
            {t('auth.login')}
          </Button>

          <Text size="sm" ta="center" c="dimmed">
            <Text
              component="span"
              c={VIPSO_RED}
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/login')}
            >
              {t('auth.useDifferentNumber')}
            </Text>
          </Text>
        </Stack>
      </form>
    </LoginLandingLayout>
  );
}
